import { useRef, type ComponentPropsWithoutRef, type ChangeEvent } from 'react';
import CandidatesBoardScreen from './CandidatesBoardScreen';
import CandidatesBoardSearch from './CandidatesBoardSearch';
import useFetchCandidateBoardsDetails from '../api/useFetchCandidateBoardsDetails';
import useFetchCandidateBoardsUncategorized from '../api/useFetchCandidateBoardsUncategorized';
import { useDebounce } from '@/hooks/useDebounce';

type Props = {
  search: Omit<
    ComponentPropsWithoutRef<typeof CandidatesBoardSearch>,
    | 'handleKeyword'
    | 'handleOrganizationUserIds'
    | 'keyword'
    | 'organizationUserIds'
    | 'boards'
    | 'boardsRefetch'
  >;
  keyword: string;
  organizationUserIds?: number[];
  onSearchChange: (change: { keyword?: string; organizationUserIds?: number[] }) => void;
};

export default function CandidatesBoardWrapper({
  search,
  keyword,
  organizationUserIds = [],
  onSearchChange,
}: Props) {
  const debounce = useDebounce(500);
  const boardScreenRef = useRef<{ scrollToEnd: () => void } | null>(null);
  const initialQueryRef = useRef({ keyword, organization_user_ids: organizationUserIds });
  const {
    boards,
    boardsData,
    boardStates,
    boardsRefetch,
    reloadBoards,
    loadMoreBoard,
    setKeyword,
    setOrganizationUserIds,
    isBoardsListFetched,
    isLoading: isBoardsLoading,
    filterKey,
  } = useFetchCandidateBoardsDetails(initialQueryRef.current);
  const {
    data: uncategorizedData,
    isLoading: isUncategorizedLoading,
    setKeyword: setUncategorizedKeyword,
    setOrganizationUserIds: setUncategorizedOrganizationUserIds,
    uncategorizedRefetch,
    hasNextPage: uncategorizedHasNextPage,
    isFetching: uncategorizedIsFetching,
  } = useFetchCandidateBoardsUncategorized(initialQueryRef.current);

  const handleKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debounce(() => {
      setKeyword(value);
      setUncategorizedKeyword(value);
      onSearchChange({ keyword: value });
    });
  };

  const handleOrganizationUserIds = (ids: number[]) => {
    setOrganizationUserIds(ids);
    setUncategorizedOrganizationUserIds(ids);
    onSearchChange({ organizationUserIds: ids });
  };

  const scrollToEndAfterBoardAdded = async () => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    await boardsRefetch();
    boardScreenRef.current?.scrollToEnd();
  };

  return (
    <div>
      <CandidatesBoardSearch
        screenType={search.screenType}
        setScreenType={search.setScreenType}
        keyword={initialQueryRef.current.keyword}
        organizationUserIds={organizationUserIds}
        handleKeyword={handleKeyword}
        handleOrganizationUserIds={handleOrganizationUserIds}
        onBoardAdded={scrollToEndAfterBoardAdded}
        boards={boards}
        boardsRefetch={boardsRefetch}
      />
      <div className="mt-8">
        <CandidatesBoardScreen
          ref={boardScreenRef}
          boards={boards.records}
          boardsData={boardsData}
          boardStates={boardStates}
          uncategorized={uncategorizedData}
          boardsRefetch={boardsRefetch}
          reloadBoards={reloadBoards}
          uncategorizedRefetch={uncategorizedRefetch}
          onLoadMoreBoard={loadMoreBoard}
          uncategorizedHasNextPage={uncategorizedHasNextPage}
          uncategorizedIsFetching={uncategorizedIsFetching}
          isLoading={!isBoardsListFetched || isBoardsLoading || isUncategorizedLoading}
          filterKey={filterKey}
        />
      </div>
    </div>
  );
}
